<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <export
      url="/tours/csv"
      :filter="totalFilter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="csvTitle"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>
  <div class="main-area h-full">
    <div class="ml-12">
      <div class="table">
        <data-table
          class="mb-auto"
          v-model:sort-by="sortKey"
          v-model:sort-direction="sortDirection"
          :row-link="(reg) => reg?.driver ? ({ name: 'driver-details', params: { driverId: reg?.driver?.id } }) : undefined"
          :busy="loading"
          :items="records"
          :columns="columns"
          :visible-columns="visibleColumns"
        />
      </div>
    </div>
  </div>
  <pagination v-model:page="page" v-model:per-page="perPage" class="pagination" :total="total" />
</template>

<script lang="tsx" setup>
import { ref, reactive, computed, unref } from 'vue';
import { useI18n } from 'vue-i18n';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@utils/toolbar/panes/AddFields.vue';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import Pagination from '@utils/table/Pagination.vue';
import DataTable, { type Column } from '@utils/table/Table.vue';
import {
  useProvider,
  collOptionsProvider,
  type Filter as ProviderFilter,
  ProviderParams
} from '@utils/table/hooks/provider';
import { format } from 'date-fns';
import type { Filter } from '@utils/filter/Filter.vue';
import type { Tour, HydraCollection, Campaign, Region, License } from '@/entities/Entities';
import { formatTimeDurationFromSeconds } from '@/utils/helpers';
import Export from '@utils/toolbar/panes/Export.vue';
import { useQueryRef } from '@/hooks/url';
import Tabs from '@components/structure/navigation/Tabs.vue';
import {useTableFields} from '@hooks/table-configuration';

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 10);
const filter = useQueryRef<ProviderFilter>('filter', {});

const totalFilter = computed(() => {
  return { ...filter.value };
});

const sortKey = useQueryRef<string | undefined>('sortKey', undefined);
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', undefined);

const { t } = useI18n();

const csvTitle = computed(() => {
  return t('csv.title.tours');
});

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter: totalFilter,
  pagination: {
    page,
    perPage,
  },
});

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    group: t('pages.tours.fieldGroups.general'),
    class: 'whitespace-nowrap',
    visible: false,
    sortable: false,
  },
  {
    key: 'createdAt',
    title: t('tour.createdAt'),
    group: t('pages.tours.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => format(new Date(v), 'dd.MM.yyyy - HH:mm:ss'),
    filter: {
      type: 'datetime',
      group: t('pages.tours.fieldGroups.general'),
      options: {
        label: t('tour.createdAt'),
      },
    },
  },
  {
    key: 'uploadedAt',
    title: t('uploadedAt'),
    group: t('pages.tours.fieldGroups.general'),
    sortable: true,
    visible: false,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-',
    filter: {
      type: 'datetime',
      group: t('pages.tours.fieldGroups.general'),
      options: {
        label: t('uploadedAt'),
      },
    },
  },
  {
    key: 'driver.user.firstName',
    title: t('user.firstName'),
    sortable: true,
    visible: true,
    group: t('pages.tours.fieldGroups.general'),
    filter: {
      type: 'text',
      group: t('pages.tours.fieldGroups.general'),
      options: {
        label: t('user.firstName'),
      },
    },
  },
  {
    key: 'driver.user.lastName',
    title: t('user.lastName'),
    group: t('pages.tours.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.tours.fieldGroups.general'),
      options: {
        label: t('user.lastName'),
      },
    },
  },
  {
    key: 'driver.user.email',
    title: t('user.email'),
    group: t('pages.tours.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.tours.fieldGroups.general'),
      options: {
        label: t('user.email'),
      },
    },
  },
  {
    key: 'license',
    title: t('licenses.license'),
    group: t('pages.tours.fieldGroups.general'),
    sortable: false,
    visible: true,
    computed: (tour: Tour) =>
      tour.license ?
      `${tour.license?.company?.name} - ${tour.license?.startDate ? format(new Date(tour.license?.startDate), 'dd.MM.yyyy') : ''} - ${tour.license?.endDate ? format(new Date(tour.license?.endDate), 'dd.MM.yyyy') : ''}`
      : '-'
  },
  {
    key: 'exists[license]',
    title: t('pages.tours.filters.forBusiness'),
    group: t('pages.tours.fieldGroups.general'),
    sortable: false,
    visible: false,
    computed: (item: Tour) => (item.license !== null) ? t('yes') : t('no'),
    filter: {
      type: 'bool',
      group: t('pages.tours.fieldGroups.general'),
      options: {
        label: t('pages.tours.filters.forBusiness'),
      },
    },
  },
  {
    key: 'exists[campaignRegistration]',
    title: t('pages.tours.filters.campaign'),
    group: t('pages.tours.fieldGroups.general'),
    sortable: false,
    visible: false,
    computed: (item: Tour) => (item.campaignRegistration !== null) ? t('yes') : t('no'),
    filter: {
      type: 'bool',
      group: t('pages.tours.fieldGroups.general'),
      options: {
        label: t('pages.tours.filters.campaign'),
      },
    },
  },
  {
    key: 'distance',
    title: t('tour.distance'),
    group: t('pages.tours.fieldGroups.general'),
    sortable: true,
    visible: true,
    unit: 'km',
    formatter: (v: number) => (v / 1000).toFixed(2) + ' km',
    filter: {
      type: 'range',
      group: t('pages.tours.fieldGroups.general'),
      options: {
        icon: <span>km</span>,
        label: t('tour.distance'),
        transformOut: (v: number) => v * 1000,
        transformIn: (v: number) => v / 1000,
      },
    },
  },
  {
    key: 'time',
    title: t('tour.time'),
    group: t('pages.tours.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: number) => formatTimeDurationFromSeconds(v),
    filter: {
      type: 'duration',
      group: t('pages.tours.fieldGroups.general'),
      options: {
        placeholderFrom: 'hh:mm:ss',
        placeholderTo: 'hh:mm:ss',
        label: t('tour.time'),
      },
    },
  },
  {
    key: 'averageSpeed',
    title: t('tour.speed'),
    group: t('pages.tours.fieldGroups.calculations'),
    sortable: true,
    visible: true,
    unit: 'km/h',
    formatter: (v: number) => (v * 3.6).toFixed(2) + ' km/h',
    filter: {
      type: 'range',
      group: t('pages.tours.fieldGroups.calculations'),
      options: {
        icon: <span>km/h</span>,
        label: t('tour.speed'),
        step: 'any',
        min: '0',
        transformOut: (v: number) => v / 3.6,
        transformIn: (v: number) => v * 3.6,
      },
    },
  },
  {
    key: 'kcal',
    title: t('tour.kcal'),
    group: t('pages.tours.fieldGroups.calculations'),
    sortable: true,
    visible: true,
    unit: 'kcal',
    filter: {
      type: 'range',
      group: t('pages.tours.fieldGroups.calculations'),
      options: {
        icon: <span>kcal</span>,
        label: t('tour.kcal'),
      },
    },
  },
  {
    key: 'co2',
    title: t('tour.co2'),
    group: t('pages.tours.fieldGroups.calculations'),
    sortable: true,
    visible: true,
    unit: 'kg',
    filter: {
      type: 'range',
      group: t('pages.tours.fieldGroups.calculations'),
      options: {
        icon: <span>t</span>,
        label: t('tour.co2'),
      },
    },
  },
];

const visibleColumns = useTableFields(
    columns,
);

const { data: items, loading } = useProvider<HydraCollection<Tour>>('/tours', providerParams);

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter))
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    return [{ key: col.key, ...col.filter }];
  })
);
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
</style>
